import { accountApiBase, fetchWithConnectionAuth } from "@/app/connection";
import { isProductionFeatureProfile } from "@/app/featureProfile";

/**
 * QA review is a staging-only surface.
 *
 * The check deliberately avoids a full `https://staging.api.ato.run` literal:
 * a Dev Preview shell must pass the exact-origin gate in
 * `scripts/validate-shell-build.mjs`, which rejects any other hosted API
 * origin found in the bundle. The staging API host is recognised by its
 * host marker instead, and only when the build is staging or a dev build.
 */
const stagingApi = () => {
  const base = accountApiBase();
  return base.includes("//staging.") && base.includes("api.ato.run");
};

export const qaVisible = () =>
  !isProductionFeatureProfile() && stagingApi();

export interface QaReview {
  current_version: string;
  review: null | {
    attempt_id: string;
    published_version: string;
    policy: string;
    current: boolean;
    expired: boolean;
    completed_at: string;
    valid_until: string;
    decision: "PASS" | "FAIL" | "HOLD";
    human_reviewed: boolean;
    reasons: string[];
    summary: string;
    limitations: string[];
    issues: {
      severity: string;
      scope: string;
      title: string;
      reproduction: string;
      improvement: string;
      confidence: string;
    }[];
    tasks: { task_id: string; title: string; status: string }[];
    agent: { name: string; version: string; model: string };
  };
}
export interface QaAttempt {
  attempt: {
    id: string;
    status: string;
    plan_json: string;
    plan_sha256: string;
    app_id: string;
    published_version: string;
  };
  sessions: {
    id: string;
    ordinal: number;
    status: string;
    expires_at: string;
    closed_at: string | null;
  }[];
}
export interface QaSession {
  id: string;
  status: string;
  attempt_id: string;
  ordinal: number;
  instance_id: string;
  app_url: string;
  expires_at: string;
  published_version: string;
  statistics_excluded: boolean;
}
export interface PublicReview {
  id: string;
  version_ref: string;
  reviewer_id: string;
  reviewer_type: "human" | "agent";
  reviewer_name: string;
  relationship: "user" | "publisher" | "agent";
  agent_model: string | null;
  qa_attempt_id: string | null;
  rating: number | null;
  title: string;
  body: string;
  confidence: "high" | "medium" | "low";
  created_at: string;
  updated_at: string;
  revision: number;
  mine: boolean;
  provisional: boolean;
  legacy: boolean;
}
export interface ReviewAggregate {
  count: number;
  rated_count: number;
  unrated_count: number;
  publisher_count: number;
  average: number | null;
}
export interface AppReviews {
  current_version: string;
  version_ref: string;
  current: boolean;
  versions: string[];
  reviews: PublicReview[];
  ai: ReviewAggregate;
  human: ReviewAggregate;
  viewer: { can_review: boolean };
  qa: {
    attempt_id: string;
    decision: "PASS" | "FAIL" | "HOLD";
    expired: boolean;
    passed: number;
    total: number;
  } | null;
}
async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  if (!qaVisible()) throw new Error("QA is available on staging only.");
  const response = await fetchWithConnectionAuth(
    `${accountApiBase()}/v1/qa${path}`,
    {
      method,
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    },
  );
  const data = await response.json();
  if (!response.ok)
    throw new Error(
      typeof data.error === "string"
        ? data.error
        : data.error?.issues
            ?.map((i: { message: string }) => i.message)
            .join(" ") || "Reviews could not load.",
    );
  return data as T;
}
export const qaClient = {
  reviews: (id: string, version?: string, signal?: AbortSignal) =>
    request<AppReviews>(
      `/apps/${encodeURIComponent(id)}/reviews${version ? `?version_ref=${encodeURIComponent(version)}` : ""}`,
      "GET",
      undefined,
      signal,
    ),
  saveReview: (
    id: string,
    version: string,
    expectedRevision: number,
    review: { rating: number | null; title: string; body: string },
  ) =>
    request<PublicReview>(
      `/apps/${encodeURIComponent(id)}/reviews/mine`,
      "PUT",
      {
        version_ref: version,
        expected_revision: expectedRevision,
        review: { ...review, confidence: "medium", evidence_refs: [] },
      },
    ),
  report: (id: string, attempt: string, signal?: AbortSignal) =>
    request<QaReview>(
      `/apps/${encodeURIComponent(id)}/reports/${encodeURIComponent(attempt)}`,
      "GET",
      undefined,
      signal,
    ),
  review: (id: string, signal?: AbortSignal) =>
    request<QaReview>(
      `/apps/${encodeURIComponent(id)}/review`,
      "GET",
      undefined,
      signal,
    ),
  attempt: (id: string, signal?: AbortSignal) =>
    request<QaAttempt>(
      `/attempts/${encodeURIComponent(id)}`,
      "GET",
      undefined,
      signal,
    ),
  start: async (id: string, requestId: string, ordinal: number) => {
    const result = await request<QaSession>(
      `/attempts/${encodeURIComponent(id)}/sessions`,
      "POST",
      { request_id: requestId, ordinal },
    );
    const url = new URL(result.app_url);
    if (
      url.protocol !== "https:" ||
      !/^cinst-[a-z0-9]+\.stg-app\.ato\.run$/.test(url.hostname) ||
      !result.statistics_excluded
    )
      throw new Error("Invalid QA surface.");
    return result;
  },
  close: (id: string) =>
    request<{ id: string; status: string; resources_released: boolean }>(
      `/sessions/${encodeURIComponent(id)}`,
      "DELETE",
    ),
};
